/**
 * form-embed D8 — a studio's general form on a website page: the studio's questions, then the visitor's
 * details and Send, in one step by default (Gabriel 2026-10-26); a site may choose two, the questions then
 * Next (Gabriel 2026-10-27). A signed-in visitor just sends; a guest adds a name and an email, or signs in
 * with Telegram from the offer, which keeps every answer. Placed by the form's website name
 * (`data-form="drum-request"`); Telify's own page `/forms/<siteKey>` draws it under the form's name and
 * description, so the piece draws no title. The form's own setting (D12) says whether it asks where to reply,
 * and picks the words that promise one.
 *
 * site-form-buy-mode: placed with a product (`data-product`), its button holds the answers and opens checkout with
 * the product in the bag; the answers reach the studio only once the order is paid.
 *
 * `standard-only`: a site restyles it through its parts, states and tokens, never by redrawing it, since a
 * drawing would have to draw any studio's questions.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'site-form',
  description: "One of the studio's general forms: its questions, then the visitor's details and Send. Signed-in visitors just send; guests add a name and an email.",
  placeable: true,
  drawing: 'standard-only',
  options: {
    form: {
      type: 'string',
      default: null,
      maxLength: 40,
      requiredWhenPlaced: true,
      description: "The form's website name, set on its On your website page (for example drum-request). Telify's page takes it from its address.",
    },
    steps: {
      type: 'enum',
      values: ['one', 'two'],
      default: 'one',
      description: "one: the questions, then the visitor's details and Send. two: the questions and Next, then the details and Send.",
    },
    detailsHeading: {
      type: 'string',
      default: null,
      maxLength: 80,
      description: 'The heading over a guest\'s details on a form that asks where to reply. Absent: "Where should we reply?".',
    },
    yourDetailsHeading: {
      type: 'string',
      default: 'Your details',
      maxLength: 80,
      description: "The heading over the visitor's details on a form that does not ask where to reply.",
    },
    // Gabriel 2026-10-31: a signed-in visitor is told where the reply comes, not asked.
    signedInHeading: {
      type: 'string',
      default: null,
      maxLength: 80,
      description:
        'The heading over the details of a signed-in visitor, on a form that asks where to reply; {studio} is the studio\'s name. Absent: "{studio} will reply to you in Telegram".',
    },
    signedInLine: {
      type: 'string',
      default: null,
      maxLength: 200,
      description:
        'The line saying who the visitor is signed in as; {name} is their name, shown bold, and {studio} the studio\'s name. Absent: "Signed in with Telegram as {name}."',
    },
    nextLabel: { type: 'string', default: 'Next', maxLength: 40, description: 'The Next button text, with two steps.' },
    sendLabel: { type: 'string', default: 'Send', maxLength: 40, description: 'The Send button text.' },
    note: {
      type: 'string',
      default: null,
      maxLength: 160,
      description: 'A line under Send, always followed by the Privacy link. Absent: "Your details go only to {studio}."',
    },
    doneHeading: { type: 'string', default: 'Sent', maxLength: 80, description: 'The heading after the form is sent.' },
    doneSignedIn: {
      type: 'string',
      default: null,
      maxLength: 200,
      description: 'The line after a signed-in visitor sends. Absent: "You\'ll hear back in Telegram."',
    },
    doneGuest: {
      type: 'string',
      default: null,
      maxLength: 200,
      description: 'The line after a guest sends; {email} is the email they typed. Absent: "You\'ll hear back at {email}."',
    },
    doneThanks: {
      type: 'string',
      default: 'Thanks. {studio} has your answers.',
      maxLength: 200,
      description: "The line after a form that does not ask where to reply is sent; {studio} is the studio's name.",
    },
    // Gabriel 2026-10-31: the site's offer lines are its standard words; one page may set its own.
    offerLines: {
      type: 'string',
      default: null,
      maxLength: 1250,
      description:
        "This placement's own lines for the sign-in offer, in place of the site's, separated by |: one to six lines of at most 200 characters; {studio} is the studio's name. Absent, or any line too long: the site's lines.",
    },
    product: {
      type: 'string',
      default: null,
      maxLength: 64,
      description:
        "A product's handle (site-form-buy-mode): the form leads into buying it. Its button holds the answers, puts the product in the bag carrying them and opens checkout; the studio gets them only once the order is paid. It asks no name or email (checkout does), counts as a form that asks where to reply, and reads Sold out when none are left or the product is not on sale (hidden, or no longer sold). The product must have one option.",
    },
    paidLine: {
      type: 'string',
      default: null,
      maxLength: 200,
      description: "With product: a line the shop's thank-you page shows under its title, for the order made with these answers.",
    },
  },
  parts: {
    form: { description: 'The whole piece.', required: false },
    questions: { description: "The studio's questions.", required: true },
    question: { description: 'One question: its words, its hint and its answer.', required: false },
    label: { description: "A question's words.", required: false },
    hint: { description: "A question's description, when the studio wrote one.", required: false },
    choice: { description: 'One choice of a choice question.', required: false, states: ['selected'] },
    next: { description: 'With two steps: Next, after the questions.', required: false, role: 'primary-action' },
    back: { description: 'With two steps: Back to the questions, every answer kept.', required: false, role: 'secondary-action' },
    details: { description: "The visitor's details after the questions (or after Next): the offer and the guest fields, or who they are signed in as.", required: false },
    detailsHeading: { description: "The heading over the visitor's details.", required: false, role: 'title' },
    offer: { description: 'The offer to sign in with Telegram, for a guest.', required: false },
    offerSignIn: { description: 'Sign in with Telegram.', required: false, role: 'secondary-action' },
    offerTestAccount: { description: 'Development only: sign in as the test account.', required: false },
    or: { description: 'The line between the offer and the guest fields: or send it as a guest.', required: false },
    guestName: { description: "The guest's name field.", required: false },
    guestEmail: { description: "The guest's email field.", required: false },
    signedIn: { description: 'Who the visitor is signed in as.', required: false },
    send: { description: 'Send; with a product, the button that leads into buying, reading Sold out when none are left.', required: true, states: ['disabled', 'soldOut'], role: 'primary-action' },
    note: { description: 'The line under Send.', required: false },
    privacy: { description: "The Privacy link after the note, to Telify's privacy policy on the site's own address.", required: true },
    error: { description: 'The line above Send when a send could not go through yet; the answers stay.', required: false },
    changed: { description: 'The line saying the form was updated while it was filled in; the answers that still fit stay.', required: false },
    done: { description: 'The screen after the form is sent.', required: true },
    doneHeading: { description: 'The heading after the form is sent.', required: false, role: 'title' },
    doneText: { description: 'The line under it.', required: false },
    openBot: { description: "After a signed-in send on a form that asks where to reply, the line to open the studio's bot and press Start, when the studio cannot message them yet.", required: false },
  },
  actions: {
    next: { description: 'With two steps: checks the questions as Send would and, when they pass, shows the details and Send.', args: [{ name: 'responses', kind: 'answers' }] },
    back: { description: 'With two steps: back to the questions, every answer kept.', args: [] },
    signIn: { description: 'Signs the visitor in with Telegram; the answers stay.', args: [] },
    signInAsTestAccount: { description: 'Development only: signs in as the test account; the answers stay.', args: [] },
    setGuestName: { description: "Sets the guest's name.", args: [{ name: 'name', kind: 'value', type: 'string', format: 'text', maxLength: 100 }] },
    setGuestEmail: { description: "Sets the guest's email.", args: [{ name: 'email', kind: 'value', type: 'string', format: 'text', maxLength: 254 }] },
    send: { description: "Sends the visitor's answers, with the guest's name and email when not signed in.", args: [{ name: 'responses', kind: 'answers' }] },
    retry: { description: 'Reads the form again after it could not be read.', args: [] },
  },
  events: {
    sent: 'The visitor sent the form.',
  },
});

/** What the form on a website page knows. */
export interface SiteFormState {
  /**
   * loading until the form is read; form while it is filled in; sending while Send waits and goes; sent after;
   * error when it could not be read (Try again); unavailable when the form is not on this website.
   */
  readonly status: 'loading' | 'form' | 'sending' | 'sent' | 'error' | 'unavailable';
  /** Which step shows: the questions, or the visitor's details and Send. One step shows both and stays on questions. */
  readonly step: 'questions' | 'details';
  /** Whether the form asks where to reply (D12): it picks the words that promise a reply. */
  readonly asksReply: boolean;
  /** The signed-in visitor's name, or null for a guest, who adds a name and an email. */
  readonly signedInAs: string | null;
  /** What the guest typed. */
  readonly guestName: string;
  readonly guestEmail: string;
  /** Why the guest's name or email was refused, or null. */
  readonly guestNameError: string | null;
  readonly guestEmailError: string | null;
  /** The line above Send when a send could not go through yet, or null. */
  readonly error: string | null;
  /** True after the form was updated while it was filled in. */
  readonly changed: boolean;
  /** After a signed-in send: the studio's bot to open and press Start when the studio cannot message them yet, else null. */
  readonly openBot: string | null;
  /** Development only: true when this server offers the test-account sign-in. */
  readonly testAccount: boolean;
  /** site-form-buy-mode: placed with a product, the form leads into buying it. */
  readonly buying: boolean;
  /** Placed with a product none are left of: the questions are put away and the button reads Sold out. */
  readonly soldOut: boolean;
}

/** What a drawing can ask the form on a website page to do. */
export interface SiteFormActions {
  /** With two steps: the questions passed (their submit calls it with their responses); show the details and Send. */
  readonly next: (responses: readonly { readonly fieldId: string; readonly value: unknown }[]) => void;
  /** With two steps: back to the questions, every answer kept. */
  readonly back: () => void;
  /** Sign in with Telegram; the answers stay. */
  readonly signIn: () => void;
  /** Development only: sign in as the test account; null when the server has none. The answers stay. */
  readonly signInAsTestAccount: (() => void) | null;
  /** Set the guest's name. */
  readonly setGuestName: (name: string) => void;
  /** Set the guest's email. */
  readonly setGuestEmail: (email: string) => void;
  /** Send the answers (the questions' submit calls it with their responses). */
  readonly send: (responses: readonly { readonly fieldId: string; readonly value: unknown }[]) => void;
  /** Read the form again after it could not be read. */
  readonly retry: () => void;
}

/** What the form on a website page tells its host. */
export interface SiteFormEvents {
  /** The visitor sent the form. */
  readonly sent: { readonly formId: string };
}
